import { useEffect, useRef, useState } from "react";
import { Bell, Check } from "lucide-react";
import { api } from "@/lib/api";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0, whatsapp_enabled: false });
  const ref = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/notifications");
      setData(r.data);
    } catch {
      // ignore — likely not authenticated yet
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // poll every 30s
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markAllRead = async () => {
    await api.post("/notifications/mark-all-read");
    load();
  };

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    load();
  };

  const timeAgo = (iso) => {
    const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return `${Math.floor(sec / 86400)}d ago`;
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative w-10 h-10 rounded-full border border-[#E5E3DB] bg-white flex items-center justify-center hover:bg-[#F3F1ED]"
        data-testid="notification-bell"
      >
        <Bell className="w-4 h-4 text-[#1A1A1A]" />
        {data.unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-[#E06D53] text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-unread-count">
            {data.unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-2xl border border-[#E5E3DB] shadow-xl overflow-hidden z-50" data-testid="notification-panel">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E3DB]">
            <div className="font-display text-lg">Notifications</div>
            {data.unread > 0 && (
              <button onClick={markAllRead} className="text-xs text-[#1E3F20] font-semibold hover:text-[#E06D53]" data-testid="notification-mark-all-read">
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {data.items.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-[#737373]">No notifications yet</div>
            ) : (
              data.items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => markRead(n.id)}
                  className={`w-full text-left px-5 py-3 border-b border-[#F3F1ED] hover:bg-[#F9F8F6] ${n.read ? "" : "bg-[#FFF6F2]"}`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-display-medium text-sm">{n.title}</div>
                    <span className="text-[10px] text-[#737373] whitespace-nowrap">{timeAgo(n.created_at)}</span>
                  </div>
                  <div className="text-xs text-[#4A4A4A] mt-1 leading-relaxed">{n.body}</div>
                  {n.read && <div className="text-[10px] text-[#737373] mt-1 inline-flex items-center gap-1"><Check className="w-3 h-3" /> read</div>}
                </button>
              ))
            )}
          </div>
          <div className="px-5 py-3 text-[10px] text-[#737373] bg-[#F9F8F6] border-t border-[#E5E3DB]">
            Need help? <a href="https://wa.me/919015334444" target="_blank" rel="noopener noreferrer" className="text-[#25D366] font-semibold">Chat with OCTA on WhatsApp</a>
          </div>
        </div>
      )}
    </div>
  );
}
